import { MeterTaskInputType } from 'switch-shared/modules/crud/meter-task/meter-task-input';
import { MeterTaskWatchEvent } from 'switch-shared/modules/crud/meter-task/meter-task.dto';
import { Serialized } from 'switch-shared/types/serialised';

import { baseAPI } from './baseAPI.service';
import { ENDPOINT_URLS } from './config/apiTags';
import { createStreamingQuery, StreamingResponse } from './streaming.helper';

/** The meter or gateway a task is watched through, whose permissions the watch is checked against. */
export type TaskScope = { meterId: string } | { gatewayId: string };

/** A task to watch, and the scope it is watched through. */
export interface WatchedTask {
  taskId: string;
  scope: TaskScope;
}

/** Tasks watched together on one stream through the same scope: the arg of `watchMeterTasks`. */
export interface TaskWatch {
  scope: TaskScope;

  /** Sorted, so that the same tasks share one stream. */
  taskIds: string[];
}

export type TaskWatchEvent = Serialized<MeterTaskWatchEvent>;

/** A key that is equal for equal scopes. */
export const scopeKey = (scope: TaskScope) =>
  'meterId' in scope ? `meter:${scope.meterId}` : `gateway:${scope.gatewayId}`;

/** The watches the tasks share, one per scope, keyed by `scopeKey`. */
export const watchesOf = (tasks: WatchedTask[]) => {
  const byScope = new Map<string, TaskWatch>();
  for (const { scope, taskId } of tasks) {
    const key = scopeKey(scope);
    const watch = byScope.get(key) ?? { scope, taskIds: [] };
    watch.taskIds.push(taskId);
    byScope.set(key, watch);
  }
  for (const watch of byScope.values()) watch.taskIds.sort();
  return byScope;
};

/** The cached data a task of each type writes to when it completes. */
const PRODUCED_TAGS: Partial<Record<MeterTaskInputType, ENDPOINT_URLS[]>> = {
  'dlms-billing': [
    ENDPOINT_URLS.BILLING_REGISTER_DATA,
    ENDPOINT_URLS.UTILITY_DATA_WORKSPACE,
  ],
  'dlms-events': [
    ENDPOINT_URLS.METER_LOG,
    ENDPOINT_URLS.UTILITY_DATA_WORKSPACE,
  ],
  'dlms-profile-discovery': [
    ENDPOINT_URLS.UTILITY_DATA_WORKSPACE,
    ENDPOINT_URLS.BILLING_REGISTER_DATA,
    ENDPOINT_URLS.METER_LOG,
  ],
};

/** The cache tags a completed task of the type invalidates, to refetch what it wrote. */
export const producedTags = (task: MeterTaskInputType) =>
  PRODUCED_TAGS[task] ?? [];

export const meterTaskAPI = baseAPI.injectEndpoints({
  endpoints: (builder) => ({
    watchMeterTasks: builder.query<
      StreamingResponse<TaskWatchEvent>,
      TaskWatch
    >({
      ...createStreamingQuery<TaskWatch, TaskWatchEvent>({
        url: ({ scope, taskIds }) =>
          `${
            'meterId' in scope
              ? `${ENDPOINT_URLS.UTILITY_DATA_WORKSPACE}/${scope.meterId}`
              : `${ENDPOINT_URLS.GATEWAY}/${scope.gatewayId}`
          }/tasks/events?ids=${taskIds.join(',')}`,
      }),
      // Each stream holds a connection, so one is closed as soon as nothing watches it.
      keepUnusedDataFor: 0,
    }),
  }),
});

export const { useWatchMeterTasksQuery } = meterTaskAPI;
